var pluginsPageInit = require("./client/platform.plugins.js");
var accountPageInit = require("./client/platform.account.js");
var appConfigPageInit = require("./client/platform.app.config.js");

// ─── Shared logout + quick action wiring ────────────────────────────────────

function attachSharedDashboardBehavior(context) {
  var logout = document.getElementById("dashboard-logout");
  if (logout) {
    logout.addEventListener("click", async function() {
      try {
        await fetch("/auth/logout", { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      } catch (e) {}
      window.location.href = "/auth/login";
    });
  }

  document.querySelectorAll("button[data-dashboard-action],button[data-target-id]").forEach(function(btn) {
    btn.addEventListener("click", function() {
      var action = (this.getAttribute("data-dashboard-action") || "").trim().toLowerCase();
      var targetId = (this.getAttribute("data-target-id") || "").trim();
      if (targetId) {
        var target = document.getElementById(targetId);
        if (target) target.click();
        return;
      }
      if (action === "refresh") window.location.reload();
    });
  });

  // Mobile sidebar toggle
  var sidebarToggle = document.getElementById("dashboard-sidebar-toggle");
  var sidebar = document.getElementById("dashboard-sidebar");
  if (sidebarToggle && sidebar) {
    sidebarToggle.addEventListener("click", function() {
      var isOpen = sidebar.classList.toggle("sidebar-open");
      sidebarToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
    // Close sidebar when a nav link is clicked on mobile
    sidebar.querySelectorAll("a.dashboard-link").forEach(function(link) {
      link.addEventListener("click", function() {
        sidebar.classList.remove("sidebar-open");
      });
    });
  }
}

// ─── Dashboard list view: init all ua.dashboard.list elements on the page ───

/**
 * Find and initialise every ua.dashboard.list element on the current page.
 * Each element carries its page namespace via data-dashboard-page-namespace.
 */
async function initDashboardLists(context) {
  var dashboardApi = (context || {}).dashboardApi;
  if (!dashboardApi) return;

  var listEls = document.querySelectorAll("[data-ua-dashboard-list]");
  for (var i = 0; i < listEls.length; i++) {
    await initSingleList(listEls[i], dashboardApi);
  }
}

async function initSingleList(el, dashboardApi) {
  var pageNamespace = (el.getAttribute("data-dashboard-page-namespace") || "").trim();
  if (!pageNamespace) return;

  // api-url and page-url are set by content functions via context.page.routes.*
  // They encode the correct source-aware URL (e.g. /api/my/... or /api/p/.../...)
  var apiUrl   = (el.getAttribute("data-dashboard-api-url")  || "").trim() || null;
  var pageUrl  = (el.getAttribute("data-dashboard-page-url") || "").trim() || null;

  // Build a scoped API client if an explicit api-url is present
  var api = apiUrl ? dashboardApi.withBase(apiUrl) : null;

  var viewMode = (el.getAttribute("data-dashboard-view-mode") || "list").trim();
  var pageSize = parseInt(el.getAttribute("data-dashboard-page-size") || "25", 10) || 25;
  var emptyMessage = el.getAttribute("data-dashboard-empty-message") || "No items found.";

  var searchInput = el.querySelector("[data-dashboard-search]");
  var filterSelects = el.querySelectorAll("[data-dashboard-filter]");
  var resultsHost = el.querySelector("[data-dashboard-results]");
  var paginationHost = el.querySelector("[data-dashboard-pagination]");
  var countHost = el.querySelector("[data-dashboard-count]");
  var viewToggleBtns = el.querySelectorAll("[data-dashboard-viewmode]");

  var state = {
    page: 1,
    limit: pageSize,
    viewMode: viewMode,
    filters: {},
    total: 0
  };

  // View toggle
  viewToggleBtns.forEach(function(btn) {
    btn.addEventListener("click", function() {
      viewToggleBtns.forEach(function(b) { b.classList.remove("active"); });
      this.classList.add("active");
      state.viewMode = (this.getAttribute("data-dashboard-viewmode") || "list").trim();
      el.setAttribute("data-dashboard-view-mode", state.viewMode);
      state.page = 1;
      loadList();
    });
  });

  // Search (debounced)
  var searchTimer = null;
  if (searchInput) {
    searchInput.addEventListener("input", function() {
      clearTimeout(searchTimer);
      var val = this.value;
      searchTimer = setTimeout(function() {
        state.filters.search = val;
        state.page = 1;
        loadList();
      }, 300);
    });
  }

  // Filter selects
  filterSelects.forEach(function(sel) {
    sel.addEventListener("change", function() {
      var filterName = this.getAttribute("data-dashboard-filter");
      state.filters[filterName] = this.value;
      state.page = 1;
      loadList();
    });
  });

  async function loadList() {
    renderSkeleton(resultsHost);
    if (countHost) countHost.textContent = "";

    try {
      // Use scoped api (with explicit api-url) when available, else fall back to namespace-based call
      var response = api
        ? await api.list(state.filters, state.page, {}, state.limit)
        : await dashboardApi.list(pageNamespace, state.filters, state.page, {}, state.limit);

      if (!response || !response.success) {
        renderError(resultsHost, "Unable to load data.");
        return;
      }

      var data = response.data || {};
      var rows = data.rows instanceof Array ? data.rows : [];
      state.total = typeof data.total === "number" ? data.total : rows.length;
      state.page  = typeof data.page  === "number" ? data.page  : state.page;

      if (countHost) {
        var start = ((state.page - 1) * state.limit) + 1;
        var end   = Math.min(state.page * state.limit, state.total);
        countHost.textContent = state.total > 0 ? ("Showing " + start + "–" + end + " of " + state.total) : "";
      }

      if (rows.length === 0) {
        if (resultsHost) resultsHost.innerHTML = "<p class='text-muted py-3 text-center'>" + escapeHtml(emptyMessage) + "</p>";
      } else {
        await renderRows(resultsHost, rows, pageNamespace, state.viewMode, pageUrl, apiUrl);
      }

      renderPagination(paginationHost, state, loadList);
    } catch (err) {
      console.error("Dashboard list load error:", err);
      renderError(resultsHost, "An error occurred while loading.");
    }
  }

  // Initial load
  await loadList();

  // Wire delete buttons (event delegation on the host element)
  el.addEventListener("click", function(e) {
    var deleteBtn = e.target.closest("[data-dashboard-delete]");
    if (!deleteBtn) return;
    e.preventDefault();
    var deleteId    = (deleteBtn.getAttribute("data-delete-id")    || "").trim();
    var deleteLabel = (deleteBtn.getAttribute("data-delete-label") || deleteId).trim();
    var ns          = (deleteBtn.getAttribute("data-page-namespace") || pageNamespace).trim();
    var btnApiUrl   = (deleteBtn.getAttribute("data-api-url") || apiUrl || "").trim() || null;
    confirmDelete(ns, deleteId, deleteLabel, loadList, dashboardApi, btnApiUrl);
  });
}

// ─── Render helpers ──────────────────────────────────────────────────────────

function renderSkeleton(host) {
  if (!host) return;
  var rows = "";
  for (var i = 0; i < 4; i++) {
    rows += "<div class='ua-dashboard-skeleton-row'></div>";
  }
  host.innerHTML = "<div class='ua-dashboard-list-skeleton'>" + rows + "</div>";
}

function renderError(host, message) {
  if (!host) return;
  host.innerHTML = "<div class='alert alert-danger mb-0'>" + escapeHtml(message) + "</div>";
}

async function renderRows(host, rows, pageNamespace, viewMode, pageUrl, apiUrl) {
  if (!host) return;
  var slug    = pageNamespace.replace(/\./g, "-");
  var baseUrl = pageUrl || ("/dashboard/" + slug);

  if (viewMode === "card") {
    await renderCardView(host, rows, baseUrl, pageNamespace, apiUrl);
    return;
  }

  // Default: list view
  var html = "<div class='ua-dashboard-list-items'>";
  for (var i = 0; i < rows.length; i++) {
    html += renderListItemHtml(rows[i], baseUrl, pageNamespace, apiUrl);
  }
  html += "</div>";
  host.innerHTML = html;
}

function renderListItemHtml(row, baseUrl, pageNamespace, apiUrl) {
  var id = (row.id || row.namespace || "").toString();
  var title = escapeHtml((row.title || row.name || row.email || id).toString());
  var subtitle = escapeHtml((row.subtitle || row.email || row.description || row.namespace || "").toString());
  var badge = row.badge || "";
  var badgeClass = row.badgeClass || (row.enabled === true ? "bg-success" : row.enabled === false ? "bg-secondary" : "");
  var icon = row.icon || "";

  if (!badge && typeof row.enabled === "boolean") {
    badge = row.enabled ? "Enabled" : "Disabled";
    badgeClass = row.enabled ? "bg-success" : "bg-secondary";
  }

  var iconHtml = "";
  if (icon) {
    var iconClass = icon.indexOf("bi-") === 0 ? ("bi " + icon) : ("bi bi-" + icon);
    iconHtml = "<div class='ua-dashboard-item-icon'><i class='" + iconClass + "'></i></div>";
  }

  var badgeHtml = badge ? ("<span class='badge " + escapeHtml(badgeClass) + "'>" + escapeHtml(badge) + "</span>") : "";

  var metaHtml = "";
  if (row.meta) {
    metaHtml = "<div class='ua-dashboard-item-meta small text-muted'>" + escapeHtml(row.meta.toString()) + "</div>";
  }

  var editHref = baseUrl.replace(/\/$/, "") + "/" + encodeURIComponent(id);

  var actionHtml =
    "<div class='ua-dashboard-item-actions'>" +
    "<a href='" + editHref + "' class='btn btn-sm btn-outline-primary' title='Edit'><i class='bi bi-pencil'></i></a>" +
    "<button type='button' class='btn btn-sm btn-outline-danger' title='Delete'" +
    " data-dashboard-delete='true'" +
    " data-delete-id='" + escapeHtml(id) + "'" +
    " data-delete-label='" + escapeHtml(title) + "'" +
    " data-page-namespace='" + escapeHtml(pageNamespace) + "'" +
    " data-api-url='" + escapeHtml(apiUrl || "") + "'>" +
    "<i class='bi bi-trash3'></i></button>" +
    "</div>";

  return "<div class='ua-dashboard-item'>" +
    iconHtml +
    "<div class='ua-dashboard-item-body'>" +
    "<div class='ua-dashboard-item-title'>" + title + "</div>" +
    (subtitle ? "<div class='ua-dashboard-item-subtitle'>" + subtitle + "</div>" : "") +
    metaHtml +
    "</div>" +
    badgeHtml +
    actionHtml +
    "</div>";
}

async function renderCardView(host, rows, baseUrl, pageNamespace, apiUrl) {
  var html = "<div class='row g-3'>";
  for (var i = 0; i < rows.length; i++) {
    var row = rows[i];
    var id = (row.id || row.namespace || "").toString();
    var title = escapeHtml((row.title || row.name || row.email || id).toString());
    var desc = escapeHtml((row.description || row.subtitle || "").toString());
    var badge = row.badge || (typeof row.enabled === "boolean" ? (row.enabled ? "Enabled" : "Disabled") : "");
    var badgeClass = row.badgeClass || (row.enabled === true ? "bg-success" : "bg-secondary");
    var editHref = baseUrl.replace(/\/$/, "") + "/" + encodeURIComponent(id);

    html += "<div class='col-sm-6 col-lg-4'>" +
      "<div class='card h-100'>" +
      "<div class='card-body'>" +
      "<div class='d-flex justify-content-between align-items-start mb-2'>" +
      "<h6 class='card-title mb-0'>" + title + "</h6>" +
      (badge ? "<span class='badge " + escapeHtml(badgeClass) + "'>" + escapeHtml(badge) + "</span>" : "") +
      "</div>" +
      (desc ? "<p class='card-text small text-muted'>" + desc + "</p>" : "") +
      "</div>" +
      "<div class='card-footer d-flex justify-content-end gap-2'>" +
      "<a href='" + editHref + "' class='btn btn-sm btn-outline-primary'><i class='bi bi-pencil me-1'></i>Edit</a>" +
      "<button type='button' class='btn btn-sm btn-outline-danger'" +
      " data-dashboard-delete='true'" +
      " data-delete-id='" + escapeHtml(id) + "'" +
      " data-delete-label='" + escapeHtml(title) + "'" +
      " data-page-namespace='" + escapeHtml(pageNamespace) + "'" +
      " data-api-url='" + escapeHtml(apiUrl || "") + "'>" +
      "<i class='bi bi-trash3'></i></button>" +
      "</div>" +
      "</div>" +
      "</div>";
  }
  html += "</div>";
  host.innerHTML = html;
}

function renderPagination(host, state, onPage) {
  if (!host) return;
  var totalPages = Math.ceil(state.total / state.limit) || 1;
  if (totalPages <= 1) {
    host.innerHTML = "";
    return;
  }

  var prevDisabled = state.page <= 1;
  var nextDisabled = state.page >= totalPages;

  host.innerHTML =
    "<span class='text-muted small'>Page " + state.page + " of " + totalPages + "</span>" +
    "<div class='btn-group btn-group-sm'>" +
    "<button type='button' class='btn btn-outline-secondary' id='dash-prev'" + (prevDisabled ? " disabled" : "") + ">Prev</button>" +
    "<button type='button' class='btn btn-outline-secondary' id='dash-next'" + (nextDisabled ? " disabled" : "") + ">Next</button>" +
    "</div>";

  var prev = host.querySelector("#dash-prev");
  var next = host.querySelector("#dash-next");
  if (prev && !prevDisabled) {
    prev.addEventListener("click", function() {
      state.page = Math.max(1, state.page - 1);
      onPage();
    });
  }
  if (next && !nextDisabled) {
    next.addEventListener("click", function() {
      state.page = Math.min(totalPages, state.page + 1);
      onPage();
    });
  }
}

// ─── Delete confirmation via ua.dialog / UATools ─────────────────────────────

function confirmDelete(pageNamespace, id, label, onSuccess, dashboardApi, apiUrl) {
  var message = "Are you sure you want to delete <strong>" + escapeHtml(label) + "</strong>? This action cannot be undone.";

  // Use UATools confirm dialog if available
  if (window.UATools && typeof window.UATools.ShowConfirm === "function") {
    window.UATools.ShowConfirm(
      "Delete " + label,
      message,
      async function() {
        await doDelete(pageNamespace, id, onSuccess, dashboardApi, apiUrl);
      }
    );
    return;
  }

  // Fallback: render a ua.dialog inline
  var dialogId = "dash-delete-confirm-" + Date.now();
  var dialogJson = {
    n: "ua.dialog",
    title: "Delete " + label + "?",
    AlertStyle: "confirm",
    allowclose: true,
    inner: message,
    footer: [
      {
        n: "button",
        type: "button",
        id: dialogId + "-confirm",
        c: "btn btn-danger",
        i: [{ n: "i", c: "bi bi-trash3 me-1" }, "Delete"]
      },
      {
        n: "button",
        type: "button",
        id: dialogId + "-cancel",
        c: "btn btn-secondary",
        "data-dismiss": "dialog",
        i: "Cancel"
      }
    ]
  };

  if (window.UATools && typeof window.UATools.render === "function") {
    var rendered = window.UATools.render(dialogJson);
    if (rendered && rendered.nodeType) {
      document.body.appendChild(rendered);
      var confirmBtn = document.getElementById(dialogId + "-confirm");
      var cancelBtn = document.getElementById(dialogId + "-cancel");
      if (confirmBtn) {
        confirmBtn.addEventListener("click", async function() {
          if (rendered.parentNode) rendered.parentNode.removeChild(rendered);
          await doDelete(pageNamespace, id, onSuccess, dashboardApi, apiUrl);
        });
      }
      if (cancelBtn) {
        cancelBtn.addEventListener("click", function() {
          if (rendered.parentNode) rendered.parentNode.removeChild(rendered);
        });
      }
      return;
    }
  }

  // Last resort: native confirm
  if (window.confirm("Delete " + label + "?")) {
    doDelete(pageNamespace, id, onSuccess, dashboardApi, apiUrl);
  }
}

async function doDelete(pageNamespace, id, onSuccess, dashboardApi, apiUrl) {
  try {
    var response = apiUrl
      ? await dashboardApi.withBase(apiUrl).delete(id)
      : await dashboardApi.delete(pageNamespace, id);
    if (response && response.success) {
      if (typeof onSuccess === "function") onSuccess();
    } else {
      showDashboardError((((response || {}).errors || [])[0]) || null, "Delete failed.");
    }
  } catch (err) {
    console.error("Dashboard delete error:", err);
    showDashboardError(err, "An error occurred during deletion.");
  }
}

// ─── Wizard detail pages: load existing record via DashboardAPI.get() ────────

async function initDashboardDetailPage(context) {
  var dashboardApi  = (context || {}).dashboardApi;
  var dashCtx       = (context || {}).dashboard || {};
  var recordId      = (dashCtx.currentRecordId || "").toString().trim();
  var currentPage   = dashCtx.currentPage || {};
  var pageNamespace = (currentPage.namespace || "").toString().trim();
  var apiUrl        = ((currentPage.routes || {}).api || "").toString().trim() || null;

  if (!dashboardApi || !recordId || recordId === "new" || !pageNamespace) return;

  // Load the record and populate UA controls via UATools.load()
  try {
    var response = apiUrl
      ? await dashboardApi.withBase(apiUrl).get(recordId)
      : await dashboardApi.get(pageNamespace, recordId);
    if (response && response.success && response.data) {
      if (window.UATools && typeof window.UATools.load === "function") {
        window.UATools.load(response.data);
      }
    }
  } catch (err) {
    console.warn("Dashboard detail load error:", err);
  }
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function escapeHtml(value) {
  return (value || "").toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function showDashboardError(error, fallbackMessage) {
  var title = (error && (error.title || error.namespace)) || "Dashboard Error";
  var body = (error && (error.description || error.message)) || fallbackMessage || "An error occurred.";
  var footer = (error && error.namespace) ? ("Namespace: " + error.namespace) : "";

  if (window.UATools && typeof window.UATools.ShowErrorMessage === "function") {
    window.UATools.ShowErrorMessage(title, "<p>" + escapeHtml(body) + "</p>", escapeHtml(footer), true);
    return;
  }
  alert(body);
}

// ─── Module export: register all pageinit handlers ───────────────────────────

module.exports = async function(options) {
  var pageinit = (options || {}).pageinit || (((window.UATools || {}).pageinit) || null);
  if (!(pageinit && typeof pageinit.register === "function")) return;

  // Wildcard: runs on every dashboard page
  pageinit.register("dashboard", "*", async function(context) {
    context = context || {};
    context.dashboard = context.dashboard || context.context || {};
    context.dashboardApi = context.dashboardApi || (window.DashboardAPI || ((((window.UATools || {}).dashboard || {}).api) || null));

    attachSharedDashboardBehavior(context);

    // Init any ua.dashboard.list elements present on this page
    await initDashboardLists(context);

    // If we're on a detail/wizard view, pre-populate form fields
    await initDashboardDetailPage(context);

    // Load dashboard-level error screens into the wizard error registry
    if (window.wizard && typeof window.wizard.registerErrors === "function" && context.dashboardApi) {
      try {
        var errScreenResp = await context.dashboardApi.list("platform.error.screens", {}, 1, {}, 1000);
        if (errScreenResp && errScreenResp.success) {
          window.wizard.registerErrors(((errScreenResp.data || {}).rows || []), 1);
        }
      } catch (e) { /* silently skip if not yet configured */ }
    }

    // Page-level overrides (plugin/app pages can provide page.errors array/object)
    if (window.wizard && typeof window.wizard.registerErrors === "function") {
      var currentPage = (context.dashboard || {}).currentPage || {};
      var source = (currentPage.source || "").toString().toLowerCase();
      var priority = source === "app" ? 3 : (source === "plugin" ? 2 : 1);
      var pageErrors = currentPage.errors || null;
      if (pageErrors) {
        if (pageErrors instanceof Array) {
          window.wizard.registerErrors(pageErrors, priority);
        } else if (typeof pageErrors === "object") {
          var arr = [];
          Object.keys(pageErrors).forEach(function(namespace) {
            var item = pageErrors[namespace];
            if (!item || typeof item !== "object") return;
            arr.push({
              namespace: namespace,
              title: item.title || "",
              description: item.description || "",
              actions: item.actions || null,
              close: item.close !== false
            });
          });
          if (arr.length > 0) window.wizard.registerErrors(arr, priority);
        }
      }
    }
  });

  // Platform: Plugins page
  pageinit.register("dashboard", "platform.plugins", async function(context) {
    await pluginsPageInit(context || {});
  });

  // Platform: Account/user pages (passkeys only — profile and roles are pure wizard pages)
  var accountNamespaces = [
    "platform.settings.passkeys"
  ];
  for (var i = 0; i < accountNamespaces.length; i++) {
    (function(ns) {
      pageinit.register("dashboard", ns, async function(context) {
        await accountPageInit(context || {});
      });
    })(accountNamespaces[i]);
  }

  // Platform: App config page
  pageinit.register("dashboard", "platform.app.config", async function(context) {
    await appConfigPageInit(context || {});
  });
};
